import { line, readingWindow, saidUnder, type ReadingWindow } from "./shared";
import { touchCloseHint } from "../../input/touch";

/*
 * A chronicle, opened (chunk U-0's split of `panels.ts`; chunk U-2's move into
 * the reading window).
 *
 * The simulation's own written lines over the records they cite — and the ids
 * are deliberately hidden here where the journal shows its own, because a book
 * is a thing somebody wrote and a journal is a thing somebody checked.
 */

/**
 * Where a title's chrome tail is hung off it, and therefore where the head
 * splits.
 *
 * The caller composes one string — *"a book by Oddmar in Eiroddcrag — left year
 * 148 day 12"*, *"masterwork oak book · 4 recorded"* — and the reading window's
 * head is two lines, so the tail becomes the italic under-line (the lab's own
 * call: *"the reading window's head IS the split"*). The two connectives are the
 * ones the client already writes; the LAST one wins, so a title that happens to
 * contain a dash inside a name still splits at the chrome.
 *
 * **This is chrome surgery on the client's own string and never on the
 * simulation's.** Every proper noun in the title is the simulation's word,
 * joined by this connective one file over (`witness/witness.ts`'s `readBook`);
 * nothing sim-composed is taken apart, which is the rule the inventory's labels
 * are held to. It degrades to *no split at all*: a title with neither connective
 * is one line and there is no under-line, so a caller that changes its
 * punctuation loses an italic line rather than a word.
 */
const TITLE_TAILS: readonly string[] = [" — ", " · "];

/**
 * The book, opened: one line per record it cites.
 *
 * Phase 5 decision 5's read side. Every line here is `sim-core`'s, composed in
 * the **written** register over the book's own citations — no contractions, the
 * year spelled out, the ground under it the town the book stands in
 * (`world/types.ts`, {@link WitnessSource.chronicleLines}). The client puts
 * them on a page and composes nothing, which is the same bargain the market
 * board's notices are read under.
 *
 * **The centred reading window since chunk U-2** (owner call 2026-09-01): a book
 * left in the world is read on the same page the book of eras is, over a
 * mid-weight veil, with its record lines ruled off one another and the key that
 * shuts it printed outside the frame. It was a corner panel until then, and the
 * corner is what the ruling took away — an interactable a player *stops* at is
 * not a readout in the margin.
 *
 * **Not a reading panel** (`hud/hud.ts`'s `reading`), and that is a decision
 * rather than an oversight: a book is walked up to and read standing in front
 * of it with the pointer still locked, exactly as a board is, because closing
 * it is the same `F` that opened it and a player who walked up to a thing did
 * not ask for their mouse back. What is in the reading list is what the player
 * *sat down* with — the journal, the map, the trace view, and the one card that
 * asks them to type. That is also why there is **no `backdrop-filter` on this
 * page** (plan decision D6): blur is allowed where the pointer is free, and here
 * it is not, so what pulls the eye is a veil tint and nothing that composites.
 *
 * There are no record tags on the page. A book is the world talking, and the
 * board learned in wave 4 of the voice overhaul what happens when a diegetic
 * surface carries handle numbers. The ids are one keypress away in the trace
 * view, which is where a debug affordance belongs.
 */
export class BookPanel {
  private readonly frame: ReadingWindow;

  constructor(root: HTMLElement, touch = false) {
    this.frame = readingWindow({
      surface: "book",
      title: "hud__panel-title",
      under: "hud__reading-under",
    });
    this.frame.hint.textContent = touch ? touchCloseHint("interact") : "F closes";

    root.append(this.frame.element);
  }

  get open(): boolean {
    return !this.frame.element.hidden;
  }

  /** Puts a book's pages on the page and shows it. */
  show(title: string, lines: readonly string[]): void {
    const cut = splitTitle(title);

    this.frame.title.textContent = cut.title;
    saidUnder(this.frame.under, cut.under);
    this.frame.body.replaceChildren(
      ...(lines.length === 0
        ? [line("the pages are blank")]
        : lines.map((text) => {
            const page = document.createElement("div");
            page.className = "hud__book-line reading-line";
            page.textContent = text;

            return page;
          })),
    );
    this.frame.element.hidden = false;
    // Every opening starts at the top of the page: the panel is filled again for
    // every book rather than mutated, so a second one must not open at the first
    // one's scroll position (`ErasPanel`'s rule for a turn).
    this.frame.body.scrollTop = 0;
  }

  hide(): void {
    this.frame.element.hidden = true;
  }
}

/**
 * Cuts a composed title into the head's two lines. See {@link TITLE_TAILS}.
 *
 * The last connective wins and the search is for a *whole* separator with its
 * spaces, so a name with a middot in it — there is none, but the rule should not
 * depend on that — cannot be mistaken for the chrome.
 */
function splitTitle(title: string): { readonly title: string; readonly under: string } {
  let at = -1;
  let tail = "";

  for (const separator of TITLE_TAILS) {
    const found = title.lastIndexOf(separator);

    if (found > at) {
      at = found;
      tail = separator;
    }
  }

  return at === -1
    ? { title, under: "" }
    : { title: title.slice(0, at), under: title.slice(at + tail.length) };
}
